import { Injectable, OnDestroy } from '@angular/core';
import { GuardsCheckEnd, NavigationEnd, Router, RouterStateSnapshot } from '@angular/router';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { CompanyProps } from 'src/store/generated/company-record.generated';
import { filterNull } from 'src/util/rxjs/operators/filter-null';
import { CompanyQueryService } from './company-query.service';

function getCompanyCode(state: RouterStateSnapshot) {
  let route = state.root;
  let companyCode: string | undefined = route.params.companyCode;
  while (companyCode == null && route.firstChild != null) {
    route = route.firstChild;
    companyCode = route.params.companyCode;
  }
  return companyCode;
}

@Injectable({
  providedIn: 'root',
})
export class CompanyContextService implements OnDestroy {
  private _onDestroy$ = new Subject();

  private readonly _onGuardCheckEnd$ = new BehaviorSubject<CompanyProps | undefined>(undefined);

  private readonly _onNavigationEnd$ = new BehaviorSubject<CompanyProps | undefined>(undefined);

  get company$(): Observable<CompanyProps | undefined> {
    return this._onNavigationEnd$;
  }

  get company(): CompanyProps | undefined {
    return this._onNavigationEnd$.getValue();
  }

  get postGuardCompany(): CompanyProps | undefined {
    return this._onGuardCheckEnd$.getValue();
  }

  public filterInvalidCompany(companyCode: string | null | undefined) {
    return companyCode != null;
  }

  constructor(router: Router, companyQuery: CompanyQueryService) {
    router.events
      .pipe(
        filter((e) => e instanceof GuardsCheckEnd),
        map((e) => e as GuardsCheckEnd),
        map((e) => (e.shouldActivate ? getCompanyCode(e.state) : undefined)),
        filter((companyCode) => this.filterInvalidCompany(companyCode)),
        distinctUntilChanged(),
        filterNull(),
        switchMap((cc) =>
          companyQuery
            .getCompanies()
            .pipe(
              map((companies) =>
                companies.find((c) => c.companyCode.toLocaleLowerCase() === cc.toLocaleLowerCase()),
              ),
            ),
        ),
        takeUntil(this._onDestroy$),
      )
      .subscribe(this._onGuardCheckEnd$);

    router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        map((e) => e as NavigationEnd),
        map((e) => {
          return this.postGuardCompany;
        }),
        takeUntil(this._onDestroy$),
      )
      .subscribe(this._onNavigationEnd$);
  }

  public ngOnDestroy(): void {
    this._onDestroy$.next(null);
    this._onDestroy$.complete();
  }
}
